/**
 * BrainMissionPage — Milestone 37.4 · the guided brain-mission surface.
 *
 * Orchestrates five deterministic phases:
 *
 *   welcome     — no answers yet AND brain never approved. Locked 4c
 *                 first-time behaviour ("Continue Setup" / "Explore
 *                 Orkexra First").
 *   question    — answering a specific question (Required first, then
 *                 Recommended / Optional in catalog order).
 *   review      — every Required question has an answer and the brain
 *                 is still ``draft`` or ``pending_review``. Founder can
 *                 jump back to edit or approve.
 *   completing  — mid-mutation between approve and the handoff card.
 *   completed   — brain approved. Renders the completion handoff:
 *                   business_brain → Brand Brain
 *                   brand_brain    → Generate Your First Campaign
 *
 * The primary CTA and Pause control live on the shared MissionShell —
 * the founder always knows what happens on Enter, and pause always
 * writes memory and returns them to Executive Home safely.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  BrainMissionType,
  BrainQuestion,
  useAnswerBrainQuestion,
  useApproveBrain,
  useBrainCatalog,
  useBrainState,
  useSkipBrainQuestion,
  useSubmitBrainForReview,
} from "@/entities/brain-missions";
import { fetchBrandBrain } from "@/entities/brand-brain";
import { fetchBusinessBrain } from "@/entities/business-brain";
import { useQuery } from "@tanstack/react-query";
import { useCurrentWorkspace } from "@/entities/workspace";
import { MissionShell } from "@/features/focus-flow";
import type { Mission } from "@/entities/missions";

import { BrainCompletionHandoff } from "./BrainCompletionHandoff";
import { BrainFullEditor, type SaveSummary } from "./BrainFullEditor";
import { BrainProgressBadge } from "./BrainProgressBadge";
import { BrainReviewScreen } from "./BrainReviewScreen";
import { BrainWelcomeScreen } from "./BrainWelcomeScreen";
import { persistBrainDeferral } from "./useFirstLoginBrainRedirect";
import { isDraftAnswered, QuestionScreen } from "./QuestionScreen";

type Phase = "welcome" | "question" | "review" | "full_edit" | "completing" | "completed";

interface BrainMissionPageProps {
  overrideType?: BrainMissionType;
  /** Patch 37.4.3 — open the mission directly in the Full Brain
   *  Editor. Used by ``/business-brain/advanced`` and
   *  ``/brand-brain/advanced`` (customer label: "Edit All Answers"). */
  forceFullEdit?: boolean;
}

export function BrainMissionPage({
  overrideType,
  forceFullEdit,
}: BrainMissionPageProps = {}) {
  const params = useParams<{ type?: string }>();
  const explicitType = overrideType
    ?? (params.type === "brand" ? "brand_brain" : undefined)
    ?? (params.type === "business" ? "business_brain" : undefined);
  const routeType = explicitType ?? deriveTypeFromPathname();
  if (!routeType) return <Navigate to="/home" replace />;

  return <BrainMissionInner type={routeType} forceFullEdit={forceFullEdit} />;
}

function deriveTypeFromPathname(): BrainMissionType | null {
  if (typeof window === "undefined") return null;
  const p = window.location.pathname;
  if (p.startsWith("/brand-brain")) return "brand_brain";
  if (p.startsWith("/business-brain")) return "business_brain";
  return null;
}

function BrainMissionInner({
  type,
  forceFullEdit,
}: {
  type: BrainMissionType;
  forceFullEdit?: boolean;
}) {
  const ws = useCurrentWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  // Patch 37.4.3 — cross-mount signal for the review-required
  // banner. When the Full Brain Editor is opened via
  // ``/business-brain/advanced`` and the founder edits an approved
  // brain, ``BrainMissionInner`` unmounts on navigation back to
  // ``/business-brain``; the notice travels through react-router
  // location state so the new mount can surface it once.
  const incomingReviewNotice =
    (location.state as { reviewRequired?: { changedCount: number } } | null)
      ?.reviewRequired ?? null;

  const catalogQ = useBrainCatalog(ws?.id, type);
  const stateQ = useBrainState(ws?.id, type);
  const brainQ = useQuery({
    queryKey: ["brain-mission-aggregate", ws?.id, type],
    queryFn: async () =>
      type === "business_brain"
        ? ((await fetchBusinessBrain(ws!.id)) as unknown as Record<string, unknown>)
        : ((await fetchBrandBrain(ws!.id)) as unknown as Record<string, unknown>),
    enabled: !!ws,
  });

  const answerMut = useAnswerBrainQuestion(ws?.id ?? "", type);
  const skipMut = useSkipBrainQuestion(ws?.id ?? "", type);
  const submitMut = useSubmitBrainForReview(ws?.id ?? "", type);
  const approveMut = useApproveBrain(ws?.id ?? "", type);

  const [phase, setPhase] = useState<Phase>(forceFullEdit ? "full_edit" : "welcome");
  const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState<unknown>("");
  const [draftDirty, setDraftDirty] = useState(false);
  // Patch 37.4.3 — after a Full Edit save that reset an approved
  // brain, the parent review renders a "changes are saved, please
  // review & approve" banner. Cleared once the founder either
  // re-approves or leaves the review phase.
  const [reviewRequiredNotice, setReviewRequiredNotice] = useState<null | {
    changedCount: number;
  }>(incomingReviewNotice);
  // Once we've absorbed the notice from location state, scrub it so a
  // browser refresh does not re-surface the banner.
  useEffect(() => {
    if (incomingReviewNotice) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);
  // Tracks whether the user completed the mission in *this* session.
  // Approved brains that we simply loaded should open in review mode —
  // only a fresh approval renders the celebratory completion handoff.
  const justApprovedRef = useRef(false);

  const questions = catalogQ.data?.questions ?? [];
  const state = stateQ.data;
  const aggregate = brainQ.data as unknown as Record<string, unknown> | undefined;
  const handlePause = () => {
    // A scanner-approved proposal intentionally resets an approved Brain to
    // draft. Persist the existing workspace-scoped deferral before returning
    // Home so the first-login gate does not immediately bounce the founder
    // back into this mission and make Pause appear unresponsive.
    if (!ws) return;
    persistBrainDeferral(ws.id);
    navigate("/home", { replace: true });
  };

  const answersById = useMemo<Record<string, unknown>>(() => {
    if (!aggregate) return {};
    const map: Record<string, unknown> = {};
    for (const q of questions) {
      map[q.id] = readAnswer(aggregate, q);
    }
    return map;
  }, [aggregate, questions]);

  // Phase transitions — driven by explicit user actions in handlers.
  // The effect only handles TERMINAL and INITIAL states:
  //   * If the brain is already approved AND the founder didn't just
  //     approve in this session → open the review screen (Patch 37.4.1
  //     · existing data should open in review/edit, never restart setup).
  //   * If the founder just approved in this session → celebration
  //     handoff card ("Continue to Brand Brain →" or "Generate Your
  //     First Campaign →").
  //   * If not first-time (has answers OR skips), leave welcome
  //     immediately so the founder never sees a stale intro.
  //   * If we don't have an active question yet, pick the first
  //     unanswered one so the shell has something to render.
  useEffect(() => {
    if (!state || !catalogQ.data) return;
    // Patch 37.4.3 — when we've been asked to open directly in the
    // Full Brain Editor (``/business-brain/advanced`` /
    // ``/brand-brain/advanced``), the phase router is locked to
    // ``full_edit`` for the life of this mount. No terminal-state or
    // welcome logic runs.
    if (forceFullEdit) return;
    if (state.derived_stage === "confirmation") {
      setPhase(justApprovedRef.current ? "completed" : "review");
      return;
    }
    // Patch 37.4.1 — never render the welcome screen when the underlying
    // brain aggregate already contains answers (existing data must open
    // in review/edit, not restart setup). ``hasProgress`` includes any
    // answered or explicitly skipped question.
    const hasProgress =
      state.answered_count > 0 || state.skipped_ids.length > 0;
    if (phase === "welcome" && (hasProgress || state.eligible_for_review)) {
      setPhase(state.eligible_for_review ? "review" : "question");
      return;
    }
    if (activeQuestionId == null) {
      const target =
        state.current_question_id
        ?? questions.find(
          (q) =>
            !state.answered_ids.includes(q.id)
            && !state.skipped_ids.includes(q.id),
        )?.id
        ?? null;
      if (target) setActiveQuestionId(target);
    }
  }, [state?.derived_stage, state?.current_question_id, state?.answered_count, state?.skipped_ids.length, catalogQ.data]);

  if (!ws) {
    return <div className="p-8 text-white/70">Loading workspace…</div>;
  }
  if (catalogQ.isPending || stateQ.isPending) {
    return <div className="p-8 text-white/70">Loading your Brain mission…</div>;
  }
  if (!state || !catalogQ.data) {
    return <div className="p-8 text-white/70">We couldn&apos;t load your Brain mission.</div>;
  }

  // ── Phase: WELCOME ────────────────────────────────────────
  if (phase === "welcome") {
    return (
      <BrainWelcomeScreen
        type={type}
        onContinue={() => setPhase("question")}
      />
    );
  }

  // ── Phase: COMPLETED (approved) ───────────────────────────
  if (phase === "completed") {
    return <BrainCompletionHandoff type={type} />;
  }

  // A synthetic Mission object powers the shared MissionShell — brain
  // missions do not use the /missions collection for content state, so
  // we manufacture the shape the shell needs.
  const synthMission = {
    id: `${type}:${ws.id}`,
    workspace_id: ws.id,
    mission_type: type,
    subject_id: ws.id,
    status: "active",
    stage: state.derived_stage,
    progress: {
      current: state.answered_count,
      total: state.total_questions,
      step_key: state.current_question_id ?? "",
    },
    memory: { schema_version: 1, definition_version: 1, data: {} },
    version: state.brain_version ?? 1,
    priority: 0,
    created_by: "founder",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    started_at: new Date().toISOString(),
    next_mission_hint: null,
  } as unknown as Mission;

  // ── Phase: FULL EDIT ──────────────────────────────────────
  if (phase === "full_edit") {
    // Return targets: from the guided Focus Flow entry point, we go
    // back to the review screen. When the founder arrived via
    // ``/business-brain/advanced`` (i.e. ``forceFullEdit``), Back
    // lands them on the guided Brain Mission review instead.
    const missionRoute = type === "business_brain" ? "/business-brain" : "/brand-brain";
    const handleBackToReview = () => {
      if (forceFullEdit) {
        navigate(missionRoute);
        return;
      }
      setPhase("review");
    };
    const handleSaved = async (summary: SaveSummary) => {
      // Refetch state + aggregate so review reflects the new answers
      // and the (possibly reset) approval status.
      await Promise.all([stateQ.refetch(), brainQ.refetch()]);
      if (summary.changedCount === 0) return;
      const noticePayload = summary.approvedBrainWasReset
        ? { changedCount: summary.changedCount }
        : null;
      if (forceFullEdit) {
        // Cross-mount handoff — the receiving ``BrainMissionInner``
        // reads the notice from location state on mount.
        navigate(missionRoute, {
          state: noticePayload ? { reviewRequired: noticePayload } : undefined,
        });
      } else {
        if (noticePayload) setReviewRequiredNotice(noticePayload);
        setPhase("review");
      }
    };
    return (
      <MissionShell
        mission={{
          id: `${type}:${ws.id}`,
          workspace_id: ws.id,
          mission_type: type,
          subject_id: ws.id,
          status: "active",
          stage: state.derived_stage,
          progress: {
            current: state.answered_count,
            total: state.total_questions,
            step_key: "full_edit",
          },
          memory: { schema_version: 1, definition_version: 1, data: {} },
          version: state.brain_version ?? 1,
          priority: 0,
          created_by: "founder",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          started_at: new Date().toISOString(),
          next_mission_hint: null,
        } as unknown as Mission}
        missionTitle={type === "business_brain" ? "Business Brain" : "Brand Brain"}
        stageLabel="Full Brain Editor"
        primaryActionLabel="Back to Review"
        onPrimaryAction={handleBackToReview}
        onPause={handlePause}
        primaryActionState="idle"
        contentSize="wide"
        testIdRoot="brain-mission-shell"
        status="Edit as many answers as you like. Nothing saves until you tap Save Changes."
      >
        <BrainFullEditor
          workspaceId={ws.id}
          missionType={type}
          questions={questions}
          state={state}
          answers={answersById}
          onBackToReview={handleBackToReview}
          onSaved={handleSaved}
        />
      </MissionShell>
    );
  }

  const missionTitle =
    type === "business_brain" ? "Business Brain" : "Brand Brain";
  const stageLabel =
    phase === "review"
      ? "Review · almost approved"
      : `Question ${Math.min(state.answered_count + 1, state.total_questions)} of ${state.total_questions}`;

  // ── Phase: REVIEW ─────────────────────────────────────────
  if (phase === "review") {
    const brainStatusIsPending = state.brain_status === "pending_review";
    const brainAlreadyApproved = state.brain_status === "approved";
    const nextRouteAfterApproved =
      type === "business_brain" ? "/brand-brain" : "/campaigns/new";
    const nextLabelAfterApproved =
      type === "business_brain"
        ? "Continue to Brand Brain →"
        : "Generate Your First Campaign →";
    const primaryLabelResolved = brainAlreadyApproved
      ? nextLabelAfterApproved
      : reviewRequiredNotice
        ? "Review & Approve"
        : type === "business_brain"
          ? (brainStatusIsPending ? "Approve Business Brain" : "Submit & Approve")
          : (brainStatusIsPending ? "Approve Brand Brain" : "Submit & Approve");
    const handleApprove = async () => {
      if (brainAlreadyApproved) {
        // Already approved — the primary CTA is the natural next mission.
        navigate(nextRouteAfterApproved);
        return;
      }
      try {
        setPhase("completing");
        if (state.brain_status === "draft") {
          await submitMut.mutateAsync();
        }
        await approveMut.mutateAsync();
        justApprovedRef.current = true;
        setReviewRequiredNotice(null);
        setPhase("completed");
      } catch {
        setPhase("review");
        toast.error("We couldn't approve just yet — please try again.");
      }
    };
    const stageLabel = brainAlreadyApproved
      ? `${missionTitle} · approved`
      : "Review · almost approved";
    const status = brainAlreadyApproved
      ? "This brain is approved. Editing any answer will drop it back to draft for re-approval."
      : brainStatusIsPending
        ? "Ready to approve. You can still edit anything above."
        : "Ready to submit. You can still edit anything above.";
    return (
      <MissionShell
        mission={synthMission}
        missionTitle={missionTitle}
        stageLabel={stageLabel}
        primaryActionLabel={primaryLabelResolved}
        onPrimaryAction={handleApprove}
        onPause={handlePause}
        primaryActionState={submitMut.isPending || approveMut.isPending ? "pending" : "idle"}
        contentSize="wide"
        testIdRoot="brain-mission-shell"
        status={status}
        secondaryActionLabel={brainAlreadyApproved ? "Edit answers" : "Back to editing"}
        onSecondaryAction={() => {
          const first = questions[0];
          setActiveQuestionId(first?.id ?? null);
          setPhase("question");
        }}
      >
        <BrainReviewScreen
          questions={questions}
          state={state}
          answers={answersById}
          onEdit={(qid) => {
            setActiveQuestionId(qid);
            setPhase("question");
          }}
          onEditOneAtATime={() => {
            // Focus Flow at the first question — same behaviour as
            // the shell's "Back to editing" secondary action.
            const first = questions[0];
            setActiveQuestionId(first?.id ?? null);
            setPhase("question");
          }}
          onEditAllAnswers={() => setPhase("full_edit")}
          reviewRequiredBanner={
            reviewRequiredNotice ? (
              <div
                className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100/90"
                data-testid="brain-review-required-banner"
              >
                <p className="font-semibold text-amber-100">
                  Your changes are saved.
                </p>
                <p className="mt-1 text-xs text-amber-100/85">
                  Review and approve them before Akinex uses the updated{" "}
                  {type === "business_brain" ? "Business Brain" : "Brand Brain"}.
                </p>
              </div>
            ) : null
          }
        />
      </MissionShell>
    );
  }

  // ── Phase: QUESTION ───────────────────────────────────────
  const q = questions.find((x) => x.id === activeQuestionId) ?? questions[0];
  if (!q) {
    // No catalog questions — shouldn't happen; guard rail.
    return <div className="p-8 text-white/70">This mission has no questions.</div>;
  }

  const isRequired = q.category === "required";
  const draftIsAnswered = isDraftAnswered(q, draftValue);
  const questionPreviouslyAnswered = state.answered_ids.includes(q.id);
  const primaryLabel = "Save & Continue";
  const primaryDisabled = !draftIsAnswered && !questionPreviouslyAnswered;

  const handlePrimary = async () => {
    if (draftDirty && draftIsAnswered) {
      try {
        await answerMut.mutateAsync({
          question_id: q.id,
          value: draftValue,
        });
      } catch {
        toast.error("We couldn't save that answer — please try again.");
        return;
      }
    }
    // Advance to the next unanswered / non-skipped question in catalog
    // order. Only auto-transition to review when every question has
    // been either answered or skipped — the founder should walk through
    // every prompt (with a clear Skip on Recommended/Optional) unless
    // they explicitly ask to skip to review.
    const nextState = await stateQ.refetch();
    const s = nextState.data ?? state;
    const next = questions.find(
      (x) => !s.answered_ids.includes(x.id) && !s.skipped_ids.includes(x.id),
    )?.id ?? null;
    if (next == null && s.eligible_for_review) {
      setPhase("review");
      setActiveQuestionId(null);
      setDraftDirty(false);
      return;
    }
    setActiveQuestionId(next);
    setDraftDirty(false);
  };

  const handleSkip = async () => {
    try {
      await skipMut.mutateAsync({ question_id: q.id });
    } catch {
      toast.error("This question can't be skipped.");
      return;
    }
    const nextState = await stateQ.refetch();
    const s = nextState.data ?? state;
    const next = questions.find(
      (x) => !s.answered_ids.includes(x.id) && !s.skipped_ids.includes(x.id),
    )?.id ?? null;
    if (next == null && s.eligible_for_review) {
      setPhase("review");
      setActiveQuestionId(null);
      setDraftDirty(false);
      return;
    }
    setActiveQuestionId(next);
    setDraftDirty(false);
  };

  const canJumpToReview = state.eligible_for_review;

  return (
    <MissionShell
      mission={synthMission}
      missionTitle={missionTitle}
      stageLabel={stageLabel}
      primaryActionLabel={primaryLabel}
      onPrimaryAction={handlePrimary}
      primaryActionState={
        answerMut.isPending
          ? "pending"
          : primaryDisabled
            ? "disabled"
            : "idle"
      }
      onPause={handlePause}
      secondaryActionLabel={!isRequired ? "Skip for now" : undefined}
      onSecondaryAction={!isRequired ? handleSkip : undefined}
      contentSize="wide"
      testIdRoot="brain-mission-shell"
      status={
        state.remaining_required_ids.length > 0
          ? `${state.remaining_required_ids.length} required question${state.remaining_required_ids.length === 1 ? "" : "s"} to go`
          : "You've covered the essentials. Keep going or head to review."
      }
    >
      <div className="flex flex-col gap-6">
        <BrainProgressBadge
          type={type}
          answered={state.answered_count}
          total={state.total_questions}
        />
        {canJumpToReview && (
          <button
            type="button"
            onClick={() => {
              setActiveQuestionId(null);
              setPhase("review");
            }}
            className="self-start text-xs font-semibold uppercase tracking-[0.14em] text-cyan-300/90 hover:text-cyan-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
            data-testid="brain-jump-to-review"
          >
            Review your answers →
          </button>
        )}
        <QuestionScreen
          question={q}
          currentAnswer={answersById[q.id]}
          onDraftChange={(v, dirty) => {
            setDraftValue(v);
            setDraftDirty(dirty);
          }}
          isPending={answerMut.isPending}
        />
      </div>
    </MissionShell>
  );
}

// ── Answer read helper ─────────────────────────────────────────

function readAnswer(agg: Record<string, unknown>, q: BrainQuestion): unknown {
  const section = agg[q.section] as Record<string, unknown> | undefined;
  if (!section) return null;
  let cur: unknown = section;
  for (const part of q.field_path.split(".")) {
    if (cur == null || typeof cur !== "object") return null;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur ?? null;
}
